import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { formatTemplateCreatedDate } from './dailyMenuApi';
import {
    createCateringTemp,
    deleteCateringTemp,
    fetchCateringTempById,
    fetchCateringTemps,
    normalizeCateringTemp,
    packageItemToCreatePayload,
    syncCateringTempPackageItems,
    updateCateringTemp,
} from './cateringTempApi';
import CateringTemplateItemsEditor from './CateringTemplateItemsEditor';
import OrdersTablePanel from './OrdersTablePanel';
import OrdersTablePagination from './OrdersTablePagination';
import OrdersSortableTh from './OrdersSortableTh';
import { buildSortHandlers, parseDateSortValue, sortList, str } from './tableSortUtils';
import { confirmAction, showUndoToast } from './ui/feedback';
import { parseMealsCsv } from './mealTypesDropdown';
import './css/Menu.css';
import './css/Inventory.css';
import './css/OrdersTablePagination.css';

function formatStatusLabel(active) {
    return active ? 'Active' : 'Inactive';
}

function formatPricePerPerson(value) {
    if (value == null || value === '') return '—';
    const n = Number.parseFloat(value);
    if (!Number.isFinite(n)) return String(value);
    return `₹${n.toFixed(2)}`;
}

function getCateringTempSortValue(row, sortKey) {
    switch (sortKey) {
        case 'packageName':
            return str(row.packageName);
        case 'pricePerPerson':
            return Number.parseFloat(row.pricePerPerson) || 0;
        case 'status':
            return row.status ? 1 : 0;
        case 'createdDate':
            return parseDateSortValue(row.createdDate);
        default:
            return '';
    }
}

const CateringTemplate = ({ restaurantId, hideTitle = false, onBack }) => {
    const navigate = useNavigate();
    const [showForm, setShowForm] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [packageName, setPackageName] = useState('');
    const [description, setDescription] = useState('');
    const [pricePerPerson, setPricePerPerson] = useState('');
    const [status, setStatus] = useState('Active');
    const [saving, setSaving] = useState(false);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableSearch, setTableSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sort, setSort] = useState(null);
    const [formItems, setFormItems] = useState([]);
    const [initialFormItems, setInitialFormItems] = useState([]);
    const [loadingFormItems, setLoadingFormItems] = useState(false);

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || 'null');
        } catch (e) {
            return null;
        }
    }, []);

    const filteredPackages = useMemo(() => {
        const q = tableSearch.trim().toLowerCase();
        if (!q) return packages;
        return packages.filter((row) =>
            [row.packageName, row.description, formatPricePerPerson(row.pricePerPerson), formatStatusLabel(row.status)]
                .some((v) => String(v ?? '').toLowerCase().includes(q))
        );
    }, [packages, tableSearch]);

    useEffect(() => {
        setPage(1);
        setSort(null);
    }, [tableSearch]);

    const sortedPackages = useMemo(
        () => sortList(filteredPackages, sort, getCateringTempSortValue),
        [filteredPackages, sort]
    );

    const { handleSortAsc, handleSortDesc } = buildSortHandlers(setSort, () => setPage(1));

    const totalPages = Math.max(1, Math.ceil(sortedPackages.length / pageSize));
    const pageSafe = Math.min(Math.max(1, page), totalPages);

    useEffect(() => {
        setPage((p) => Math.min(p, totalPages));
    }, [totalPages, pageSize]);

    const pageStart = (pageSafe - 1) * pageSize;
    const pagedPackages = sortedPackages.slice(pageStart, pageStart + pageSize);

    const loadPackages = useCallback(async () => {
        if (!restaurantId) {
            setPackages([]);
            return;
        }
        setLoading(true);
        try {
            const list = await fetchCateringTemps(restaurantId);
            setPackages(list);
        } catch (err) {
            console.error('Error fetching catering packages:', err?.response?.data || err?.message);
            setPackages([]);
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        loadPackages();
    }, [loadPackages]);

    const resetForm = () => {
        setPackageName('');
        setDescription('');
        setPricePerPerson('');
        setStatus('Active');
        setFormItems([]);
        setInitialFormItems([]);
        setLoadingFormItems(false);
    };

    const handleOpenForm = (e) => {
        e.preventDefault();
        setEditingRow(null);
        resetForm();
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingRow(null);
        resetForm();
    };

    const loadFormItems = useCallback(async (tempId) => {
        if (!tempId) {
            setFormItems([]);
            setInitialFormItems([]);
            return;
        }
        setLoadingFormItems(true);
        try {
            const data = await fetchCateringTempById(tempId);
            const loaded = Array.isArray(data?.items) ? data.items : [];
            setFormItems(loaded);
            setInitialFormItems(loaded);
        } catch (err) {
            console.error('Error loading package items:', err?.response?.data || err?.message);
            setFormItems([]);
            setInitialFormItems([]);
        } finally {
            setLoadingFormItems(false);
        }
    }, []);

    const handleEdit = (row) => {
        const normalized = normalizeCateringTemp(row) || row;
        setEditingRow(normalized);
        setPackageName(normalized.packageName ?? '');
        setDescription(normalized.description ?? '');
        setPricePerPerson(
            normalized.pricePerPerson != null && normalized.pricePerPerson !== ''
                ? String(normalized.pricePerPerson)
                : ''
        );
        setStatus(normalized.status ? 'Active' : 'Inactive');
        setFormItems([]);
        setInitialFormItems([]);
        setShowForm(true);
        loadFormItems(normalized.cateringTempId);
    };

    const handleDelete = async (row) => {
        const tempId = row.cateringTempId;
        if (!tempId) return;

        const confirmed = await confirmAction({
            title: 'Delete package?',
            message: `"${row.packageName}" will be set to inactive.`,
            confirmLabel: 'Delete',
            cancelLabel: 'Keep',
            danger: true,
        });
        if (!confirmed) return;

        const prev = packages;
        setPackages((list) => list.filter((p) => p.cateringTempId !== tempId));

        showUndoToast({
            message: `Deleted "${row.packageName}"`,
            onUndo: () => setPackages(prev),
            onCommit: async () => {
                try {
                    await deleteCateringTemp(tempId);
                    await loadPackages();
                } catch (err) {
                    setPackages(prev);
                    alert(
                        err.response?.data?.error ||
                            err.response?.data?.message ||
                            err.message ||
                            'Failed to delete package.'
                    );
                }
            },
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!restaurantId) {
            alert('No restaurant selected.');
            return;
        }
        if (!packageName.trim()) {
            alert('Please enter a package name.');
            return;
        }

        for (const item of formItems) {
            if (parseMealsCsv(item.meal).length === 0) {
                alert(
                    `Select at least one meal type for "${item.itemName || 'a package item'}".`
                );
                return;
            }
        }

        const seenCateringItemIds = new Set();
        for (const item of formItems) {
            const id = String(item.cateringItemId ?? '');
            if (!id) continue;
            if (seenCateringItemIds.has(id)) {
                alert(`"${item.itemName || 'A catering item'}" is listed more than once in this package.`);
                return;
            }
            seenCateringItemIds.add(id);
        }

        const userId = Number(user?.userId) || Number(restaurantId);
        const payload = {
            restaurantId: Number(restaurantId),
            packageName: packageName.trim(),
            description: description.trim(),
            pricePerPerson: pricePerPerson.trim(),
            status: status === 'Active' ? '1' : '0',
        };

        setSaving(true);
        try {
            let response;
            if (editingRow?.cateringTempId) {
                payload.updatedBy = userId;
                response = await updateCateringTemp(editingRow.cateringTempId, payload);
                await syncCateringTempPackageItems(
                    editingRow.cateringTempId,
                    initialFormItems,
                    formItems,
                    userId
                );
            } else {
                payload.createdBy = userId;
                if (formItems.length > 0) {
                    payload.items = formItems.map((item) => packageItemToCreatePayload(item, userId));
                }
                response = await createCateringTemp(payload);
            }
            alert(
                response.data?.message ||
                    (editingRow ? 'Catering package updated.' : 'Catering package created successfully.')
            );
            handleCloseForm();
            await loadPackages();
        } catch (err) {
            alert(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    err.message ||
                    'Failed to save catering package.'
            );
        } finally {
            setSaving(false);
        }
    };

    const isEditMode = Boolean(editingRow?.cateringTempId);

    return (
        <div className="menu menu-template">
            <div className={`menu-heading${hideTitle ? ' menu-heading--templates-only' : ''}`}>
                <div className="menu-template-detail-title">
                    <h2>Catering Packages</h2>
                </div>
                <div className="menu-heading-actions">
                    <div className="addmenu-button">
                        <button type="button" className="add-button" onClick={handleOpenForm}>
                            Add Package
                        </button>
                    </div>
                </div>
            </div>

            {showForm && (
                <div
                    className="inventory-modal-overlay"
                    onClick={handleCloseForm}
                    role="presentation"
                >
                    <div
                        className="inventory-modal menu-template-modal menu-template-modal--with-items"
                        onClick={(ev) => ev.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="catering-package-modal-title"
                    >
                        <div className="inventory-modal-header menu-template-modal-header">
                            <h4 id="catering-package-modal-title">
                                {isEditMode ? 'Edit Package' : 'Add Package'}
                            </h4>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                onClick={handleCloseForm}
                                aria-label="Close"
                                disabled={saving}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <form className="inventory-form menu-template-modal-form" onSubmit={handleSave}>
                            <div className="inventory-form-group">
                                <label htmlFor="catering-package-name" className="menu-form-label--required">
                                    Package name
                                </label>
                                <input
                                    id="catering-package-name"
                                    type="text"
                                    className="add-menu-form-field"
                                    placeholder="e.g. Wedding Buffet"
                                    value={packageName}
                                    onChange={(e) => setPackageName(e.target.value)}
                                    required
                                    autoFocus
                                />
                            </div>
                            <div className="inventory-form-group">
                                <label htmlFor="catering-package-price">Price per person</label>
                                <input
                                    id="catering-package-price"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="add-menu-form-field"
                                    placeholder="Price per person"
                                    value={pricePerPerson}
                                    onChange={(e) => setPricePerPerson(e.target.value)}
                                />
                            </div>
                            <div className="inventory-form-group">
                                <label htmlFor="catering-package-status">Status</label>
                                <div className="inventory-native-select-wrap">
                                    <select
                                        id="catering-package-status"
                                        className="add-menu-form-field inventory-select-native"
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                            <div className="inventory-form-group">
                                <label htmlFor="catering-package-description">Description</label>
                                <textarea
                                    id="catering-package-description"
                                    className="add-menu-form-field"
                                    placeholder="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </div>
                            <div className="menu-template-modal-form-span-2">
                                <CateringTemplateItemsEditor
                                    restaurantId={restaurantId}
                                    items={formItems}
                                    onItemsChange={setFormItems}
                                    packageId={editingRow?.cateringTempId ?? null}
                                    deferItemPersist={isEditMode}
                                    user={user}
                                    loading={loadingFormItems}
                                    disabled={saving}
                                    showTitle={false}
                                    title="Package items"
                                    headerClassName="menu-template-form-items-header"
                                    emptyMessage="No items added yet. Click Add from menu to select catering items."
                                />
                            </div>
                            <div className="inventory-form-actions menu-template-modal-actions menu-template-modal-form-span-2">
                                <button type="submit" className="add-button" disabled={saving}>
                                    {saving ? 'Saving…' : isEditMode ? 'Update Package' : 'Create Package'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <OrdersTablePanel
                searchValue={tableSearch}
                onSearchChange={setTableSearch}
                resultCount={sortedPackages.length}
                resultLabel="package"
                searchAriaLabel="Search catering packages"
            >
                <table className="menu-table">
                    <thead>
                        <tr>
                            <OrdersSortableTh
                                label="Package Name"
                                sortKey="packageName"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <OrdersSortableTh
                                label="Price Per Person"
                                sortKey="pricePerPerson"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <OrdersSortableTh
                                label="Status"
                                sortKey="status"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <OrdersSortableTh
                                label="Created On"
                                sortKey="createdDate"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={5} className="menu-table-empty-cell menu-empty-filtered">
                                    Loading packages…
                                </td>
                            </tr>
                        ) : filteredPackages.length > 0 ? (
                            pagedPackages.map((row, index) => (
                                <tr key={row.cateringTempId ?? `${row.packageName}-${index}`}>
                                    <td>
                                        <button
                                            type="button"
                                            className="inventory-item-name-link"
                                            onClick={() =>
                                                navigate(
                                                    `/home?tab=restaurants/mytemplates/${row.cateringTempId}`
                                                )
                                            }
                                        >
                                            {row.packageName}
                                        </button>
                                    </td>
                                    <td>{formatPricePerPerson(row.pricePerPerson)}</td>
                                    <td>
                                        <span
                                            className={`menu-template-status-badge menu-template-status-badge--${
                                                row.status ? 'active' : 'inactive'
                                            }`}
                                        >
                                            {formatStatusLabel(row.status)}
                                        </span>
                                    </td>
                                    <td className="menu-table-cell-muted">
                                        {formatTemplateCreatedDate(row.createdDate)}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="action-button"
                                            title="Edit package"
                                            onClick={() => handleEdit(row)}
                                        >
                                            <FaEdit />
                                        </button>
                                        {row.status && (
                                            <button
                                                type="button"
                                                className="action-button delete-button"
                                                title="Deactivate package"
                                                onClick={() => handleDelete(row)}
                                            >
                                                <FaTrash />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="menu-table-empty-cell menu-empty-filtered">
                                    {tableSearch.trim()
                                        ? 'No packages match your search.'
                                        : 'No catering packages found.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </OrdersTablePanel>

            {!loading && filteredPackages.length > 0 ? (
                <OrdersTablePagination
                    currentPage={pageSafe}
                    totalPages={totalPages}
                    totalItems={sortedPackages.length}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={(next) => {
                        setPageSize(next);
                        setPage(1);
                    }}
                    itemLabel="packages"
                    ariaLabel="Catering packages table pagination"
                />
            ) : null}
        </div>
    );
};

export default CateringTemplate;
